import { useMemo } from 'react';
import { ASSIST_API, requestParams } from 'services/assist-api/axios';
import type { AssistQuery } from 'services/assist-api/query';
import { useQueryAssist } from 'services/assist-api/query-client';

/**
 * These are the prices for the assessment products. With the exception of
 * Teacher Workplace Survey which is a survey and has a negative price so that
 * we can display "Varies" in the UI and Me and My School which needs the price
 * displayed as "$250" in the UI.
 * This is a hack that needs to be replaced with a custom endpoint that loads
 * prices (after NZCER completed Netsuite integration).
 */
export const AREA_PRICES = {
  math: 30,
  comp: 30,
  vocab: 30,
  listening: 30,
  pg: 30,
  tws: -999999999999,
  mms: 250
};

export enum AreaType {
  Survey = 'survey',
  Assessment = 'assessment'
}

export enum PricingModel {
  Paid = 'paid',
  Free = 'free'
}

export enum CreationLimit {
  Single = 'single',
  Multiple = 'multiple'
}

export type BaseArea = API_JSON & {
  type: AreaType;
  name: string;
  alternativeName: string | null;
  identifier: string;
  description?: string | null;
  notes?: string | null;
  pricingModel: PricingModel;
  price?: number | null;
  testCreationLimit: CreationLimit;
  testDurationLimit?: number | null;
  product: API_JSON;
  enabled: boolean;
  markingEnabled: boolean;
  private: boolean;
  order: number;
};

const groups = [
  'area-type',
  'area-name',
  'area-identifier',
  'area-description',
  'area-notes',
  'area-pricingmodel',
  'area-price',
  'area-testcreationlimit',
  'area-testdurationlimit',
  'area-product',
  'area-enabled',
  'area-private'
];
/**
 * Fetch areas
 * @param params can be used to customise/limit the areas retrieved
 * @returns an array of areas
 */
export const getAreas: GetMany<BaseArea> = async (params) =>
  (await ASSIST_API.get('areas', { params: requestParams({ groups }, params) })).data;

export const getAreasWithNoDefaults: GetMany<BaseArea> = async (params) =>
  (await ASSIST_API.get('areas', { params: requestParams({}, params) })).data;

/**
 * Fetch area (singular)
 * @param params can be used to customise/limit the areas retrieved
 * @returns a single area
 */
export const getArea: GetOne<BaseArea> = async (id, params) =>
  (await ASSIST_API.get(`areas/${id}`, { params: requestParams({ groups }, params) })).data;

/**
 * Hook implementation to fetch areas
 *  - caches data
 *  - loading, error, etc
 * @param params additional options to filter the request
 * @returns areas matching request
 */
export const useAreas: AssistQuery.UseMany<BaseArea> = (params, options) =>
  useQueryAssist(['areas', params], () => getAreas(params), {
    ...options,
    staleTime: STALE_LONG,
    allowNoFilters: true
  });

export const useAreasWithNoDefaults: AssistQuery.UseMany<BaseArea> = (params, options) =>
  useQueryAssist(['areas', params], () => getAreasWithNoDefaults(params), options);

/**
 * Given an areaId fetch and return the area
 *  - only runs once areaId is provided
 *  - fetches the one area
 *  - caches data
 * @param id areaId
 * @param params optional parameters to change requested data
 * @returns the matching area
 */
export const useArea: AssistQuery.UseOne<BaseArea> = (id, params, options) =>
  useQueryAssist(['areas', { id, ...params }], () => getArea(id, params), {
    ...options,
    staleTime: STALE_LONG,
    enabled: !!id
  });

/**
 * Given an areaId fetch and return the area
 *  - fetches all areas (relatively cheap)
 *  - caches data
 * @param id areaId
 * @returns the matching area
 */
export const useAreaById = (id: number): BaseArea | undefined => {
  const { data } = useAreas();
  return useMemo(() => data?.json.find((area) => area.id === id), [data, id]);
};
